import { NmLoader } from '../NmLoader';
import { ActionButton, ActionIconButton, ActionToggleGroup } from '../ui/ActionButton';
import { athleteSettingsApi } from '../../services/athleteSettingsApi';
import { applyAthleteWorkoutZones, WorkoutZoneUnavailableError } from '../../utils/athleteWorkoutZones';
import { calculatePlannedMetricsFromBlocks } from '../../utils/plannedWorkoutMetrics';
import { IoMicOutline, IoStopOutline, IoCloseOutline, IoCheckmarkOutline, IoArrowForwardOutline, IoCreateOutline } from 'react-icons/io5';
import { displayDigits } from '../../utils/displayDigits';
import { WorkoutStructureBar } from './WorkoutStructureBar';
import './workout-ai-sheet.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiWorkoutStructure } from '../../utils/workoutStructure';
import {
  parseWorkoutBuilderSettings,
  type WorkoutBuilderSettings,
} from '../../utils/workoutBuilderSettings';
import {
  workouts,
  type GenerateWorkoutFromPromptResult,
  type PlannedMetricsPreview,
  type WorkoutAiChatMessage,
} from '../../services/api';
import { useToast, toastErrorMessage } from '../../context/ToastContext';
import { secToHMS } from '../../utils/secToHMS';
import { MobileSheet } from '../MobileSheet';
import { isRtlLocale, normalizeLocale } from '../../i18n/config';
import { useWorkoutPromptVoice } from '../../hooks/useWorkoutPromptVoice';

import { WORKOUT_AI_PROMPT_SHEET_Z } from '../../features/workout/workoutOverlayZIndex';
const WORKOUT_AI_SHEET_Z = WORKOUT_AI_PROMPT_SHEET_Z;

export interface WorkoutAiApplyPayload {
  structure: ApiWorkoutStructure;
  suggested_title: string;
  suggested_workout_type?: GenerateWorkoutFromPromptResult['suggested_workout_type'];
  planned_metrics: PlannedMetricsPreview;
  warnings: string[];
  conversation_id?: string | null;
  messages?: WorkoutAiChatMessage[];
}

export interface WorkoutAiPromptSheetProps {
  open: boolean;
  onClose: () => void;
  userId: number;
  workoutTypeId?: number | null;
  workoutSettings: WorkoutBuilderSettings;
  currentStructure?: ApiWorkoutStructure | null;
  locale?: string;
  /** Prefill the composer when opening (e.g. copilot chip). */
  initialPrompt?: string;
  /** Continue an existing server conversation from the builder panel. */
  conversationId?: string | null;
  initialMessages?: WorkoutAiChatMessage[];
  onApply: (payload: WorkoutAiApplyPayload) => void;
  /** Keep panel chat in sync while the sheet is open. */
  onConversationSync?: (payload: {
    conversation_id: string | null;
    messages: WorkoutAiChatMessage[];
  }) => void;
}

export function WorkoutAiPromptSheet({
  open,
  onClose,
  userId,
  workoutTypeId,
  workoutSettings,
  currentStructure,
  locale,
  initialPrompt,
  conversationId: conversationIdProp,
  initialMessages,
  onApply,
  onConversationSync,
}: WorkoutAiPromptSheetProps) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const requestVersion = useRef(0);
  const requestBusy = useRef(false);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<GenerateWorkoutFromPromptResult | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<WorkoutAiChatMessage[]>([]);
  const [errorText, setErrorText] = useState<string | null>(null);

  const textDir = isRtlLocale(normalizeLocale(i18n.language)) ? 'rtl' : 'ltr';
  const localeCode = locale ?? i18n.language?.split('-')[0] ?? 'fa';
  const hasConversation = !!conversationId && messages.length > 0;
  const canApply = (preview?.structure?.structure?.length ?? 0) > 0;

  const handleVoiceTranscript = useCallback((text: string) => {
    setPrompt(prev => (prev.trim() ? `${prev.trim()} ${text}` : text));
    setErrorText(null);
    requestAnimationFrame(() => promptRef.current?.focus());
  }, []);

  const voice = useWorkoutPromptVoice({
    userId,
    locale: localeCode,
    disabled: loading,
    onTranscript: handleVoiceTranscript,
    onError: msg => toast.error(msg),
  });

  useEffect(() => {
    if (!open && (voice.isRecording || voice.isRequesting || voice.isTranscribing)) {
      voice.cancelRecording();
    }
  }, [open, voice.isRequesting, voice.isRecording, voice.isTranscribing, voice.cancelRecording]);

  useEffect(() => {
    requestVersion.current++;
    requestBusy.current = false;
    setLoading(false);
    if (!open) return;
    setPreview(null);
    setErrorText(null);
    setConversationId(conversationIdProp ?? null);
    setMessages(Array.isArray(initialMessages) ? initialMessages : []);
    if (initialPrompt?.trim()) {
      setPrompt(initialPrompt.trim());
    }

    // Only hydrate when the sheet opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { requestVersion.current++; };
  }, [open, userId]);

  useEffect(() => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, loading, preview]);

  const resetConversation = useCallback(() => {
    setPreview(null);
    setConversationId(null);
    setMessages([]);
    setErrorText(null);
    setPrompt('');
    onConversationSync?.({ conversation_id: null, messages: [] });
  }, [onConversationSync]);

  const handleClose = useCallback(() => {
    ++requestVersion.current;
    requestBusy.current = false;
    setLoading(false);
    void voice.cancelRecording();
    onClose();
  }, [loading, voice.isRecording, voice.isTranscribing, voice.cancelRecording, onClose]);

  const handleGenerate = useCallback(async () => {
    if (requestBusy.current || voice.isRecording || voice.isRequesting || voice.isTranscribing) return;
    const trimmed = prompt.trim();
    if (!trimmed) {
      const msg = t('workoutBuilder.ai.emptyPrompt');
      setErrorText(msg);
      toast.error(msg);
      return;
    }
    if (!userId || userId <= 0) {
      const msg = t('workoutBuilder.ai.contextMissing');
      setErrorText(msg);
      toast.error(msg);
      return;
    }
    const version = ++requestVersion.current;
    requestBusy.current = true;
    setLoading(true);
    setErrorText(null);
    try {
      const zoneResponse = await athleteSettingsApi.getAthleteZones(userId);
      const athleteZones = zoneResponse.data.data.zones;
      const sanitizedSettings = parseWorkoutBuilderSettings(workoutSettings);
      const res = await workouts.generateFromPrompt({
        prompt: trimmed,
        user_id: userId,
        workout_type_id: workoutTypeId ?? undefined,
        locale: locale ?? i18n.language?.split('-')[0] ?? 'fa',
        conversation_id: conversationId ?? undefined,
        current_structure: (preview?.structure ?? currentStructure)?.structure?.length
          ? (preview?.structure ?? currentStructure) : undefined,
        workout_settings: {
          primaryLengthMetric: sanitizedSettings.primaryLengthMetric,
          primaryIntensityMetric: sanitizedSettings.primaryIntensityMetric,
          primaryIntensityTargetOrRange: sanitizedSettings.primaryIntensityTargetOrRange,
        },
      });
      if (version !== requestVersion.current) return;
      const data = res.data?.data;
      if (!data?.structure) {
        throw new Error(t('workoutBuilder.ai.generateFailed'));
      }
      const structure = applyAthleteWorkoutZones(data.structure as ApiWorkoutStructure, athleteZones);
      const effectiveSettings = parseWorkoutBuilderSettings(structure as Partial<WorkoutBuilderSettings>);
      const plannedMetrics = calculatePlannedMetricsFromBlocks(structure.structure, {
        primaryIntensityMetric: effectiveSettings.primaryIntensityMetric,
        primaryIntensityTargetOrRange: effectiveSettings.primaryIntensityTargetOrRange,
        ftp: Number(athleteZones.ftp) || 0,
        ltHr: Number(athleteZones.lt_hr) || 0,
        maxHr: Number(athleteZones.max_hr) || 0,
        thresholdSpeedMps: Number((athleteZones.speed_zones as { threshold?: number })?.threshold) || 0,
      });
      setPreview({ ...data, structure, planned_metrics: structure === data.structure ? data.planned_metrics : plannedMetrics });
      setConversationId(data.conversation_id ?? null);
      const nextMessages = Array.isArray(data.messages) ? data.messages : [];
      setMessages(nextMessages);
      onConversationSync?.({
        conversation_id: data.conversation_id ?? null,
        messages: nextMessages,
      });
      setPrompt('');
    } catch (err: unknown) {
      if (version !== requestVersion.current) return;
      const msg = err instanceof WorkoutZoneUnavailableError
        ? t('workoutBuilder.ai.zoneUnavailable', { zone: err.zone })
        : toastErrorMessage(err, t('workoutBuilder.ai.generateFailed'));
      setErrorText(msg);
      toast.error(msg);
    } finally {
      if (version === requestVersion.current) { requestBusy.current = false; setLoading(false); }
    }
  }, [preview, currentStructure, voice.isRecording, voice.isRequesting, voice.isTranscribing, prompt, userId, workoutTypeId, locale, i18n.language, workoutSettings, conversationId, t, toast, onConversationSync]);

  const handleApply = useCallback(() => {
    if (!preview?.structure) return;
    onApply({
      structure: preview.structure as ApiWorkoutStructure,
      suggested_title: preview.suggested_title,
      suggested_workout_type: preview.suggested_workout_type,
      planned_metrics: preview.planned_metrics ?? {},
      warnings: preview.warnings ?? [],
      conversation_id: conversationId,
      messages,
    });
    onClose();
  }, [preview, onApply, onClose, conversationId, messages]);

  const metrics = preview?.planned_metrics;
  const blockCount = preview?.structure?.structure?.length ?? 0;
  const voiceBusy = voice.isRecording || voice.isRequesting || voice.isTranscribing;
  const displayError = errorText ?? voice.errorMessage;

  return (
    <MobileSheet open={open} onClose={handleClose} zIndex={WORKOUT_AI_SHEET_Z} sheetClassName="workout-ai-sheet" title={t('workoutBuilder.ai.title')}
      header={<header className="wai-header" dir={textDir}>
        <div><span className="wai-eyebrow">{t('workoutBuilder.ai.voiceEyebrow')}</span><h3>{t('workoutBuilder.ai.title')}</h3></div>
        <ActionIconButton
          className="wai-icon-button"
          type="button"
          onClick={handleClose}
          label={t('common.close')}
          icon={<IoCloseOutline size={22} aria-hidden />}
        />
      </header>}
      footer={<div className="wai-footer" dir={textDir}>
        {preview ? <>
          <ActionButton
            type="button"
            className="wai-secondary"
            onClick={resetConversation}
            disabled={loading || voiceBusy}
          >{t('workoutBuilder.ai.newChat')}</ActionButton>
          <ActionButton
            type="button"
            className="wai-primary"
            onClick={handleApply}
            disabled={loading || voiceBusy}
            variant="primary"
          ><IoCheckmarkOutline size={20} aria-hidden />{t('workoutBuilder.ai.apply')}</ActionButton>
        </> : <ActionButton
          type="button"
          className="wai-primary"
          onClick={() => void handleGenerate()}
          disabled={loading || voiceBusy || !prompt.trim()}
          variant="primary"
          loading={loading}
          icon={<IoArrowForwardOutline size={20} aria-hidden />}
        >{loading ? t('workoutBuilder.ai.generating') : t('workoutBuilder.ai.showPreview')}</ActionButton>}
      </div>}
    >
      <div className="wai-content" dir={textDir}>
        <ol className="wai-progress" aria-label={t('workoutBuilder.ai.flowLabel')}>
          {[t('workoutBuilder.ai.describeStep'), t('workoutBuilder.ai.previewStep'), t('workoutBuilder.ai.applyStep')].map((label, index) => <li key={index} className={(preview ? index === 1 : index === 0) ? 'is-current' : ''}><span>{displayDigits(index + 1, i18n.language)}</span>{label}</li>)}
        </ol>
        {!hasConversation && <p className="wai-intro">{t('workoutBuilder.ai.subtitle')}</p>}
        {messages.length > 0 && <div ref={threadRef} className="wai-thread" aria-live="polite">{messages.map((message, index) => <div key={index} className={`wai-message wai-message--${message.role}`}>{message.content}</div>)}</div>}
        {preview && <section className="wai-preview" aria-label={t('workoutBuilder.ai.previewStep')}>
          <div className="wai-preview__status"><IoCheckmarkOutline size={18} aria-hidden />{t('workoutBuilder.ai.previewReady')}</div>
          <h4 id="wai-preview-title" tabIndex={-1}>{preview.suggested_title}</h4>
          {preview.suggested_workout_type && <p>{preview.suggested_workout_type.persian_title && localeCode === 'fa' ? preview.suggested_workout_type.persian_title : preview.suggested_workout_type.title}</p>}
          <WorkoutStructureBar structure={(preview.structure as ApiWorkoutStructure).structure ?? []} primaryIntensityMetric={parseWorkoutBuilderSettings(preview.structure as Partial<WorkoutBuilderSettings>).primaryIntensityMetric} />
          <dl className="wai-metrics">
            <div><dt>{t('workoutBuilder.ai.blocksLabel')}</dt><dd>{displayDigits(blockCount, i18n.language)}</dd></div>
            {metrics?.total_time_planned != null && metrics.total_time_planned > 0 && <div><dt>{t('workoutBuilder.ai.estimatedDuration')}</dt><dd dir="ltr">{displayDigits(secToHMS(metrics.total_time_planned), i18n.language)}</dd></div>}
            {metrics?.tss_planned != null && <div><dt>{t('workoutBuilder.ai.estimatedTss')}</dt><dd>{displayDigits(Math.round(metrics.tss_planned), i18n.language)}</dd></div>}
            {metrics?.if_planned != null && <div><dt>{t('workoutBuilder.ai.estimatedIf')}</dt><dd>{displayDigits(Number(metrics.if_planned).toFixed(2), i18n.language)}</dd></div>}
          </dl>
          {!!preview.warnings?.length && <div className="wai-warnings"><strong>{t('workoutBuilder.ai.warnings')}</strong><ul>{preview.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul></div>}
        </section>}
        <section className={`wai-voice${voice.isRecording ? ' is-recording' : ''}`} aria-label={t('workoutBuilder.ai.voiceStart')}>
          {voice.isSupported ? <>
            <ActionIconButton
              type="button"
              className="wai-microphone"
              size="lg"
              variant="primary"
              onClick={() => voice.toggleRecording()}
              disabled={loading || voice.isRequesting || voice.isTranscribing}
              aria-pressed={voice.isRecording}
              label={voice.isRecording ? t('workoutBuilder.ai.voiceStop') : t('workoutBuilder.ai.voiceStart')}
              icon={voice.isTranscribing ? <NmLoader size="xs" inline decorative /> : voice.isRecording ? <IoStopOutline size={28} aria-hidden /> : <IoMicOutline size={30} aria-hidden />}
            />
            <div className="wai-voice__copy" role="status" aria-live="polite"><strong>{voice.isRequesting ? t('workoutBuilder.ai.voiceRequesting') : voice.isRecording ? t('workoutBuilder.ai.voiceRecording') : voice.isTranscribing ? t('workoutBuilder.ai.voiceTranscribing') : t('workoutBuilder.ai.voiceInvite')}</strong><span>{voice.isRecording ? t('workoutBuilder.ai.tapToFinish') : t('workoutBuilder.ai.voiceHint')}</span></div>
          </> : <p className="wai-unsupported">{t('workoutBuilder.ai.voiceUnsupported')}<br />{t('workoutBuilder.ai.typeInstead')}</p>}
        </section>
        <div className="wai-composer">
          <label htmlFor="wai-prompt"><IoCreateOutline size={17} aria-hidden />{hasConversation ? t('workoutBuilder.ai.revisionLabel') : t('workoutBuilder.ai.promptLabel')}</label>
          <textarea id="wai-prompt" ref={promptRef} value={prompt} onChange={event => { setPrompt(event.target.value); setErrorText(null); }} placeholder={hasConversation ? t('workoutBuilder.ai.revisePlaceholder') : t('workoutBuilder.ai.promptPlaceholder')} rows={3} disabled={loading || voiceBusy} dir={textDir}
            onKeyDown={event => { if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) { event.preventDefault(); if (!loading && !voiceBusy && prompt.trim()) void handleGenerate(); } }} />
          {!hasConversation && !prompt && <ActionButton
            className="wai-example"
            type="button"
            disabled={loading || voiceBusy}
            onClick={() => { setPrompt(t('workoutBuilder.ai.examplePrompt')); promptRef.current?.focus(); }}
          >{t('workoutBuilder.ai.tryExample')}</ActionButton>}
          {hasConversation && <ActionButton
            className="wai-secondary wai-revise"
            loading={loading}
            type="button"
            onClick={() => void handleGenerate()}
            disabled={loading || voiceBusy || !prompt.trim()}
          >{loading ? t('workoutBuilder.ai.generating') : t('workoutBuilder.ai.continue')}</ActionButton>}
        </div>
        {loading && <p className="wai-loading" role="status"><NmLoader size="sm" inline label={t('workoutBuilder.ai.generating')} /></p>}
        {displayError && <p className="wai-error" role="alert">{displayError}</p>}
      </div>
    </MobileSheet>
  );
}
